import React, { useCallback, useRef, useEffect } from 'react';
import { ModalStackContext } from './modal-stack-context-definition';

export function ModalStackProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const modalStackRef = useRef<Array<{ id: string; closeHandler: () => void }>>(
    []
  );

  const registerModal = useCallback((id: string, closeHandler: () => void) => {
    // Remove any existing registration for this id
    modalStackRef.current = modalStackRef.current.filter(
      (modal) => modal.id !== id
    );
    // Add to top of stack
    modalStackRef.current.push({ id, closeHandler });
  }, []);

  const unregisterModal = useCallback((id: string) => {
    modalStackRef.current = modalStackRef.current.filter(
      (modal) => modal.id !== id
    );
  }, []);

  const closeTopModal = useCallback(() => {
    if (modalStackRef.current.length === 0) return false;

    const topModal = modalStackRef.current[modalStackRef.current.length - 1];
    topModal.closeHandler();
    return true;
  }, []);

  const isTopModal = useCallback((id: string) => {
    if (modalStackRef.current.length === 0) return false;
    const topModal = modalStackRef.current[modalStackRef.current.length - 1];
    return topModal.id === id;
  }, []);

  // Global ESC key handler
  useEffect(() => {
    const handleGlobalEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalStackRef.current.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        closeTopModal();
      }
    };

    // Use capture: true to intercept before other handlers
    document.addEventListener('keydown', handleGlobalEscape, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleGlobalEscape, {
        capture: true,
      });
    };
  }, [closeTopModal]);

  return (
    <ModalStackContext
      value={{ registerModal, unregisterModal, closeTopModal, isTopModal }}
    >
      {children}
    </ModalStackContext>
  );
}
